(() => {
  if (window.CedarAmberStore) { window.CedarAmberStore.mount(document); return; }
  const instances = new Map();
  let config = {};
  try { config = JSON.parse(document.getElementById('ca-store-config')?.textContent || '{}'); } catch {}
  document.body.classList.add('ca-store-enhanced');

  function updateCount(cart) {
    if (!Number.isFinite(cart?.item_count)) return;
    document.querySelectorAll('[data-ca-cart-count]').forEach(node => { node.textContent = String(cart.item_count); });
    document.querySelectorAll('[data-ca-cart-link]').forEach(link => link.setAttribute('aria-label', `View your bag, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`));
  }

  async function refreshCart() {
    const response = await fetch(`${config.cart || '/cart'}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not refresh the bag.');
    const cart = await response.json();
    updateCount(cart);
    return cart;
  }
  document.addEventListener('ca:cart:updated', event => {
    if (event.detail?.cart) updateCount(event.detail.cart);
    else refreshCart().catch(() => {});
  });
  document.addEventListener('cart:refresh', () => refreshCart().catch(() => {}));
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart().catch(() => {}); });
  window.CedarAmber = { ...(window.CedarAmber || {}), cart: { refresh: refreshCart } };


  function createHeader(header) {
    if (!header) return () => {};
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const home = header.closest('.ca-home-shell');
    const benefits = header.querySelector('[data-ca-benefits]');
    const toggle = header.querySelector('[data-ca-benefits-toggle]');
    const shop = header.querySelector('[data-ca-shop-menu]');
    const desktop = matchMedia('(min-width: 1100px)');
    let expiryTimer;
    let measuredHeight = 0;
    const measure = () => {
      if (abort.signal.aborted) return;
      const height = Math.ceil(header.getBoundingClientRect().height);
      if (!height || height === measuredHeight) return;
      measuredHeight = height;
      [document.documentElement, document.body, home].filter(Boolean).forEach(node => node.style.setProperty('--ca-header-height', `${height}px`));
    };
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(header);
    window.addEventListener('resize', measure, { ...options, passive: true });
    document.fonts?.ready.then(measure);
    measure();
    const closeShop = (focus = false) => {
      if (!shop?.open) return;
      shop.open = false;
      if (focus) shop.querySelector('summary')?.focus({ preventScroll: true });
    };
    shop?.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); closeShop(true); }
    }, options);
    shop?.addEventListener('click', event => { if (event.target.closest('a')) closeShop(); }, options);
    document.addEventListener('pointerdown', event => { if (shop?.open && !shop.contains(event.target)) closeShop(); }, options);
    document.addEventListener('focusin', event => { if (shop?.open && !shop.contains(event.target)) closeShop(); }, options);
    desktop.addEventListener('change', () => {
      if (!desktop.matches && shop?.open) {
        const hadFocus = shop.contains(document.activeElement);
        closeShop();
        if (hadFocus) header.querySelector('[data-ca-brand]')?.focus({ preventScroll: true });
      }
    }, options);
    toggle?.addEventListener('click', () => {
      const paused = benefits.classList.toggle('is-paused');
      toggle.setAttribute('aria-pressed', String(paused));
      toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    }, options);
    benefits?.addEventListener('focusin', event => {
      if (!event.target.closest('a')) return;
      requestAnimationFrame(() => {
        if (abort.signal.aborted) return;
        const viewport = benefits.querySelector('.ca-header-benefits__window');
        const targetRect = event.target.getBoundingClientRect();
        const viewportRect = viewport.getBoundingClientRect();
        viewport.scrollLeft += targetRect.left - viewportRect.left - (viewport.clientWidth - targetRect.width) / 2;
      });
    }, options);
    benefits?.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!benefits.contains(document.activeElement)) benefits.querySelector('.ca-header-benefits__window').scrollLeft = 0;
      });
    }, options);
    const checkExpiry = () => {
      clearTimeout(expiryTimer);
      const ends = Date.parse(benefits?.dataset.caOfferEnds || '');
      if (!Number.isFinite(ends) || abort.signal.aborted) return;
      const remaining = ends - Date.now();
      if (remaining <= 0) {
        header.querySelectorAll('[data-ca-header-offer]').forEach(node => node.remove());
        return;
      }
      expiryTimer = setTimeout(checkExpiry, Math.min(remaining, 3600000));
    };
    window.addEventListener('pageshow', checkExpiry, options);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) checkExpiry(); }, options);
    checkExpiry();
    return () => { observer?.disconnect(); clearTimeout(expiryTimer); closeShop(); abort.abort(); };
  }

  function createMenu(menu) {
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const desktop = matchMedia('(min-width: 1100px)');
    let oldOverflow = null;
    const close = (focus = true) => {
      menu.open = false;
      if (oldOverflow !== null) { document.documentElement.style.overflow = oldOverflow; oldOverflow = null; }
      if (focus) menu.querySelector('summary')?.focus();
    };
    const closeOnDesktop = () => {
      if (!desktop.matches || !menu.open) return;
      const focusWasInside = menu.contains(document.activeElement);
      close(false);
      if (focusWasInside) document.querySelector('.ca-store-brand')?.focus({ preventScroll: true });
    };
    desktop.addEventListener('change', closeOnDesktop, options);
    menu.addEventListener('toggle', () => {
      if (menu.open) {
        if (desktop.matches) { closeOnDesktop(); return; }
        if (oldOverflow === null) oldOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        menu.querySelector('[data-ca-menu-close]')?.focus();
      } else if (oldOverflow !== null) { document.documentElement.style.overflow = oldOverflow; oldOverflow = null; }
    }, options);
    menu.querySelector('[data-ca-menu-close]')?.addEventListener('click', () => close(), options);
    menu.addEventListener('click', event => { if (event.target.closest('a')) close(false); }, options);
    menu.addEventListener('keydown', event => {
      if (!menu.open) return;
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key === 'Tab') {
        const controls = [...menu.querySelectorAll('.ca-store-menu__panel a,.ca-store-menu__panel button')];
        if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls.at(-1)?.focus(); }
        else if (!event.shiftKey && document.activeElement === controls.at(-1)) { event.preventDefault(); controls[0]?.focus(); }
      }
    }, options);
    return () => { close(false); abort.abort(); };
  }

  function createCollection(section) {
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const grid = section.querySelector('[data-ca-catalog-grid]');
    const cards = [...section.querySelectorAll('[data-ca-catalog-card]')];
    const search = section.querySelector('[data-ca-local-search]');
    const sort = section.querySelector('[data-ca-sort]');
    const count = section.querySelector('[data-ca-result-count]');
    const empty = section.querySelector('[data-ca-filter-empty]');
    const params = new URLSearchParams(location.search);
    const allowed = ['all', 'outdoor', 'indoor', 'traditional', 'infrared'];
    let selected = allowed.includes(params.get('category')) ? params.get('category') : 'all';
    let catalog = [];
    try { catalog = JSON.parse(section.querySelector('[data-ca-catalog-map]')?.textContent || '[]'); } catch {}
    const ranks = new Map(catalog.map(item => [String(item.id), item.rank]));
    let searchTimer;
    const update = (saveUrl = false) => {
      const query = (search?.value || '').trim().toLocaleLowerCase();
      const visible = cards.filter(card => {
        const match = (selected === 'all' || card.dataset.category === selected || card.dataset.heating === selected) && (!query || card.dataset.search.includes(query));
        card.hidden = !match;
        return match;
      });
      const mode = sort?.value || 'manual';
      const sorted = [...cards].sort((a, b) => {
        if (mode === 'price-ascending') return Number(a.dataset.price) - Number(b.dataset.price);
        if (mode === 'price-descending') return Number(b.dataset.price) - Number(a.dataset.price);
        if (mode === 'title-ascending') return a.dataset.name.localeCompare(b.dataset.name, config.locale || 'en');
        return (ranks.get(a.dataset.id) ?? 1000 + Number(a.dataset.order)) - (ranks.get(b.dataset.id) ?? 1000 + Number(b.dataset.order));
      });
      if (grid) sorted.forEach(card => grid.appendChild(card));
      section.querySelectorAll('[data-ca-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.caFilter === selected)));
      if (count) {
        const filterLabel = selected === 'all' ? '' : ` · ${selected[0].toUpperCase()}${selected.slice(1)}`;
        count.textContent = `${visible.length} ${visible.length === 1 ? 'sauna' : 'saunas'}${filterLabel}${query ? ` matching “${query}”` : ''}`;
      }
      if (empty) empty.hidden = visible.length !== 0 || cards.length === 0;
      if (saveUrl) {
        const url = new URL(location.href);
        if (selected === 'all') url.searchParams.delete('category'); else url.searchParams.set('category', selected);
        if (mode === 'manual') url.searchParams.delete('sort_by'); else url.searchParams.set('sort_by', mode);
        history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      }
    };
    const animateUpdate = (saveUrl = false) => {
      clearTimeout(searchTimer);
      const previous = new Map();
      cards.forEach(card => {
        card.getAnimations?.().forEach(animation => animation.cancel());
        if (!card.hidden) previous.set(card, card.getBoundingClientRect());
      });
      update(saveUrl);
      let visibleIndex = 0;
      cards.forEach(card => {
        if (card.hidden || typeof card.animate !== 'function') return;
        const before = previous.get(card);
        const after = card.getBoundingClientRect();
        const x = before ? before.left - after.left : 0;
        const y = before ? before.top - after.top : 14;
        card.animate([{ transform: `translate(${x}px,${y}px)`, opacity: before ? 1 : 0 }, { transform: 'translate(0,0)', opacity: 1 }], { duration: 360, delay: before ? 0 : Math.min(visibleIndex, 4) * 30, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' });
        visibleIndex += 1;
      });
    };
    section.querySelectorAll('[data-ca-filter]').forEach(button => button.addEventListener('click', () => { selected = button.dataset.caFilter; animateUpdate(true); }, options));
    section.querySelector('[data-ca-reset-filters]')?.addEventListener('click', () => { selected = 'all'; if (search) search.value = ''; animateUpdate(true); }, options);
    search?.addEventListener('input', () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => animateUpdate(), 140); }, options);
    sort?.addEventListener('change', () => animateUpdate(true), options);
    section.querySelector('[data-ca-sort-form]')?.addEventListener('submit', event => { event.preventDefault(); animateUpdate(true); }, options);
    section.querySelectorAll('[data-ca-js-only]').forEach(element => { element.hidden = false; });
    update();
    return () => { clearTimeout(searchTimer); cards.forEach(card => card.getAnimations?.().forEach(animation => animation.cancel())); abort.abort(); };
  }

  function createContact(container) {
    const abort = new AbortController();
    const context = container.querySelector('[data-ca-contact-product]');
    const link = container.querySelector('[data-ca-contact-product-link]');
    const field = container.querySelector('[data-ca-contact-product-field]');
    const handle = new URLSearchParams(location.search).get('product');
    if (!context || !link || !field || !handle || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) return () => abort.abort();
    const routeRoot = String(config.root || '/').replace(/\/?$/, '/');
    const productUrl = new URL(`${routeRoot}products/${encodeURIComponent(handle)}`, location.origin);
    if (productUrl.origin !== location.origin) return () => abort.abort();
    fetch(`${productUrl.pathname}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: abort.signal })
      .then(response => { if (!response.ok) throw new Error('Product context is unavailable.'); return response.json(); })
      .then(product => {
        if (abort.signal.aborted || typeof product.title !== 'string' || !product.title.trim()) return;
        link.textContent = product.title;
        link.href = productUrl.href;
        field.value = `${product.title} · ${productUrl.href}`;
        field.disabled = false;
        context.hidden = false;
      })
      .catch(() => {});
    return () => abort.abort();
  }

  function createCart(form) {
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const updateButton = form.querySelector('[data-ca-cart-update]');
    const wallets = form.querySelector('[data-ca-cart-wallets]');
    const walletNotice = form.querySelector('[data-ca-cart-wallet-notice]');
    const fields = [...form.querySelectorAll('input[name="updates[]"], textarea[name="note"]')];
    const savedValues = fields.map(field => field.value);
    let submitting = false;
    function syncWallets() {
      if (!wallets) return;
      const dirty = fields.some((field, i) => field.value !== savedValues[i]);
      wallets.inert = dirty || submitting;
      wallets.setAttribute('aria-disabled', String(dirty || submitting));
      wallets.setAttribute('aria-busy', String(submitting));
      if (walletNotice) walletNotice.hidden = !dirty || submitting;
    }
    form.addEventListener('input', syncWallets, options);
    form.addEventListener('change', syncWallets, options);
    form.querySelectorAll('[data-ca-js-only]').forEach(element => { element.hidden = false; });
    form.querySelectorAll('[data-ca-cart-step]').forEach(button => button.addEventListener('click', () => {
      const input = button.closest('.ca-quantity')?.querySelector('input');
      if (!input) return;
      input.value = String(Math.max(0, (Number.parseInt(input.value, 10) || 0) + Number(button.dataset.caCartStep)));
      if (input.checkValidity()) form.requestSubmit(updateButton);
    }, options));
    form.addEventListener('submit', event => {
      if (event.defaultPrevented || event.submitter?.name !== 'update') return;
      submitting = true;
      syncWallets();
      const status = form.querySelector('[data-ca-cart-status]');
      if (status) status.textContent = 'Updating your bag…';
      form.querySelectorAll('[data-ca-cart-step]').forEach(button => { button.disabled = true; });
    }, options);
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      submitting = false;
      form.querySelectorAll('[data-ca-cart-step]').forEach(button => { button.disabled = false; });
      syncWallets();
    }, options);
    syncWallets();
    return () => abort.abort();
  }

  function mount(container) {
    const definitions = [['[data-ca-store-header]', createHeader], ['[data-ca-store-menu]', createMenu], ['[data-ca-collection]', createCollection], ['[data-ca-contact]', createContact], ['[data-ca-cart-form]', createCart]];
    definitions.forEach(([selector, create]) => {
      const elements = container.matches?.(selector) ? [container] : [...container.querySelectorAll(selector)];
      elements.forEach(element => { if (!instances.has(element)) instances.set(element, create(element)); });
    });
  }
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => { instances.forEach((destroy, element) => { if (event.target.contains(element)) { destroy(); instances.delete(element); } }); });
  window.CedarAmberStore = { mount };
  mount(document);
})();
